// M11 round one: the sign-in's new pieces, ported from the prototype's round zero (apps/prototype/src/screens/SignInSteps.tsx): the welcome
// step (after the code, for an address in no workspace), the creation form (step=new: from the welcome step, the chooser and the switcher's
// "Add a workspace") and the invitations waiting for the address.
import { useEffect, useState, type FormEvent } from "react";
import { Button, Input, InvitationRow, cx } from "@omnabase/ui";
import type { Joined, MyInvitation, WorkspaceAddress } from "@omnabase/shared";
import { ApiError, api } from "../../lib/api";
import { acceptInvitation, checkWorkspaceAddress, createWorkspace, declineInvitation, leaveWorkspace, myInvitations } from "../../lib/invitations";
import { addressPlaces, addressVerdict, creationProblem, proposeSlug } from "../../lib/signin";
import { toast } from "../../shell/toast";

const ROLE_LABEL: Record<string, string> = { owner: "Owner", admin: "Administrator", agent: "Agent" };

/** "You joined …" with "Not for you? Leave" (the brief's Q4): leaving reloads the app, which lands the person by the usual order */
export function joinedToast(joined: Joined[]): void {
  if (!joined.length) return;
  const first = joined[0]!;
  toast(`You joined ${joined.map((j) => j.name).join(" and ")}`, { kind: "success", action: { label: "Not for you? Leave", onClick: () => { void leaveWorkspace(first.id).then(() => window.location.assign("/"), (e: unknown) => toast(e instanceof Error ? e.message : "Could not leave", { kind: "error" })); } } }, 12_000);
}

/** The invitations waiting for the signed-in address: live ones with Decline and Accept, expired ones saying whom to ask. */
export function InvitationList({ onJoined }: { onJoined: (j: Joined) => void }) {
  const [list, setList] = useState<MyInvitation[] | null>(null);
  useEffect(() => { void myInvitations().then(setList, () => setList([])); }, []);
  if (!list?.length) return null;
  const accept = async (inv: MyInvitation) => {
    try { onJoined(await acceptInvitation(inv.id)); } catch (e) { toast(e instanceof Error ? e.message : "Could not accept", { kind: "error" }); }
  };
  const decline = async (inv: MyInvitation) => {
    try { await declineInvitation(inv.id); setList((l) => (l ?? []).filter((x) => x.id !== inv.id)); toast(`Invitation to ${inv.workspace.name} declined`, { kind: "success" }); } catch (e) { toast(e instanceof Error ? e.message : "Could not decline", { kind: "error" }); }
  };
  return (
    <>
      <h2 className="ob-signin__section">Invitations</h2>
      <ul className="ob-invite-list" aria-label="Invitations">
        {list.map((inv) => <InvitationRow key={inv.id} mark={inv.workspace.mark} workspace={inv.workspace.name} inviter={inv.inviter.name} role={ROLE_LABEL[inv.role]} expired={inv.expired} onAccept={() => void accept(inv)} onDecline={() => void decline(inv)} />)}
      </ul>
    </>
  );
}

/** After the code, an address with no workspace lands here, never on the email form again: its invitations, and "Create a workspace". */
export function WelcomeStep({ email, onCreate, onJoined, onNotYou }: { email: string; onCreate: () => void; onJoined: (j: Joined) => void; onNotYou: () => void }) {
  return (
    <div className="ob-signin__form">
      <h1 className="ob-signin__title">Welcome to OmnaBase</h1>
      <p className="ob-signin__intro"><strong>{email}</strong> does not belong to a workspace yet.</p>
      <InvitationList onJoined={onJoined} />
      <Button variant="primary" className="ob-signin__btn" icon="plus" onClick={onCreate}>Create a workspace</Button>
      <p className="ob-signin__foot">Expecting an invitation? Ask your colleague to send it to this address: it opens after the code. · <button type="button" className="ob-link" onClick={onNotYou}>Not you?</button></p>
    </div>
  );
}

/** the address check, asked while typing (a quarter second after the last key) */
function useAddressCheck(slug: string): WorkspaceAddress | null {
  const [answer, setAnswer] = useState<WorkspaceAddress | null>(null);
  useEffect(() => {
    if (!slug) { setAnswer(null); return; }
    let live = true;
    const t = setTimeout(() => { void checkWorkspaceAddress(slug).then((a) => { if (live) setAnswer(a); }, () => undefined); }, 250);
    return () => { live = false; clearTimeout(t); };
  }, [slug]);
  return answer?.slug === slug ? answer : null;
}
/** the terms' version in force (the box is asked only while it is dated; at version 0 a plain line) */
function useTermsVersion(): string | null {
  const [v, setV] = useState<string | null>(null);
  useEffect(() => { void api.get<{ terms: { version: string } }>("/api/v1/legal").then((l) => setV(l.terms.version), () => setV("0")); }, []);
  return v;
}

/** The address, its check's verdict and the sentence saying where it is used. The founder (2026-10-07): the verdict never replaces the
 *  sentence and nothing moves: the verdict has a line of its own under the field, reserved (hidden until there is one, one line high), and the
 *  sentence always stays, showing the typed address in both places. */
function AddressField({ slug, check, onChange }: { slug: string; check: WorkspaceAddress | null; onChange: (slug: string) => void }) {
  const where = addressPlaces(slug);
  const verdict = addressVerdict(check);
  return (
    <div className="ob-signin__address">
      <label className="ob-signin__field"><span className="ob-signin__label">Address</span>
        <Input className="ob-input--text" value={slug} onChange={(e) => onChange(e.target.value.trim().toLowerCase())} aria-label="Address" aria-describedby="ob-new-address-check ob-new-address" maxLength={40} spellCheck={false} />
      </label>
      <p id="ob-new-address-check" className={cx("ob-signin__check", !check && "ob-signin__check--none", check && (check.status === "free" ? "ob-signin__check--free" : "ob-signin__check--problem"))} aria-live="polite" title={check?.message}>{verdict ?? "\u00a0"}</p>
      <p id="ob-new-address" className="ob-signin__where">Your team opens it at <strong className="ob-signin__where-address">{where.app}</strong>, and your mail arrives at <strong className="ob-signin__where-address">{where.mail}</strong>.</p>
    </div>
  );
}

/** The creation form: the name, the address proposed from it and checked as typed, the terms box while the terms are dated. Made, the
 *  workspace opens on its Home. */
export function NewWorkspaceStep({ onCreated, onCancel, initialName = "" }: { onCreated: (slug: string) => void; onCancel: () => void; /** M12 round five: the connect page proposes the site's name */ initialName?: string }) {
  const [name, setName] = useState(initialName);
  const [typedSlug, setTypedSlug] = useState<string | null>(null); // null: the address follows the name until the person edits it
  const [terms, setTerms] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const slug = typedSlug ?? proposeSlug(name);
  const check = useAddressCheck(slug);
  const version = useTermsVersion();
  const dated = version !== null && version !== "0";

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    const why = !name.trim() ? "Name your workspace." : !slug ? "Choose an address." : check && check.status !== "free" ? check.message : dated && !terms ? "Tick the box to accept the Terms and the data processing agreement." : null;
    setProblem(why); if (why) return;
    setBusy(true);
    try { const w = await createWorkspace({ name: name.trim(), slug, ...(version ? { termsVersion: version } : {}), termsAccepted: dated ? terms : undefined }); onCreated(w.slug); }
    catch (err) { setProblem(err instanceof ApiError ? creationProblem(err.message, err.details) : "Something went wrong. Try again."); setBusy(false); }
  };

  return (
    <form className="ob-signin__form" onSubmit={(e) => void submit(e)} noValidate>
      <h1 className="ob-signin__title">Create a workspace</h1>
      <p className="ob-signin__intro">One workspace holds your team, your products and their support mail.</p>
      <label className="ob-signin__field"><span className="ob-signin__label">Workspace name</span>
        <Input className="ob-input--text" value={name} onChange={(e) => setName(e.target.value)} placeholder="Your company or product" aria-label="Workspace name" autoFocus maxLength={60} />
      </label>
      <AddressField slug={slug} check={check} onChange={setTypedSlug} />
      {dated ? (
        <label className="ob-signin__tick"><input type="checkbox" checked={terms} onChange={(e) => setTerms(e.target.checked)} />
          <span>I accept the <a className="ob-link" href="/legal/terms" target="_blank" rel="noreferrer">Terms</a> and the <a className="ob-link" href="/legal/dpa" target="_blank" rel="noreferrer">data processing agreement</a>.</span>
        </label>
      ) : <p className="ob-signin__foot">The <a className="ob-link" href="/legal/terms" target="_blank" rel="noreferrer">Terms</a> and the <a className="ob-link" href="/legal/dpa" target="_blank" rel="noreferrer">data processing agreement</a> apply to every workspace.</p>}
      {problem && <p className="ob-signin__problem" role="alert">{problem}</p>}
      <Button variant="primary" type="submit" className="ob-signin__btn" disabled={busy}>Create workspace</Button>
      <p className="ob-signin__foot"><button type="button" className="ob-link" onClick={onCancel}>Cancel</button></p>
    </form>
  );
}
